import React, { useState, useEffect, useCallback } from 'react';
import numeral from 'numeral';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';

import { showSuccessSnackbar } from 'modules/snackbar';
import { goodsinfoAdverList } from 'lib/api/goods';
import { CookieStatisticsData, cookieParamGraph } from 'lib/api/datacenter';
import { dayColor, makeRowColumn, getHeadObj } from 'lib/commonLib';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import AutoComplete from 'components/molecules/NewSelectBox/AutoComplete';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import BarGraph from 'components/organisms/Chart/BarGraph';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const useStyles = makeStyles((theme) => ({
  cellSize: {
    width: 80,
  },
}));

const CookieStatistics = (props) => {
  const { graphList } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();
  let initParams = {
    sDate: moment().add(-1, 'month').format('YYYYMMDD'),
    eDate: moment().add(-1, 'day').format('YYYYMMDD'),
    order: 'STATS_DTTM',
    sort: 'desc',
    adverId: null,
    pltfomTpCode: 'all',
  };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [reGetData, setReGetData] = useState(false);
  const [adverList, setAdverList] = useState([]);
  const initCustomized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        return dayColor(makeRowColumn(rowObj['STATS_DTTM']));
      },
    },
  };
  const [customized, setCustomized] = useState(initCustomized);

  const list = {
    LC: {
      title: t('라스트클릭'),
      tooltipTitle: t('광고 유입을 실시간성으로 확인해서 유저의 라스트클릭이 변경되었을 경우 광고 (재)송출'),
    },
    TC: {
      title: t('투데이카트'),
      tooltipTitle: t('최근 24시간 장바구니에 담은 상품'),
    },
    WC: {
      title: t('위클리카트'),
      tooltipTitle: t('최근 일주일간 장바구니에 담은 상품'),
    },
    MC: {
      title: t('먼슬리카트'),
      tooltipTitle: t('최근 한달간 장바구니에 담은 상품'),
    },
    CW: {
      title: t('카트매칭'),
      tooltipTitle: t('장바구니에 담은 상품(TC,WC,MC 이후)'),
    },
    OS: {
      title: t('온사이트서치'),
      tooltipTitle: t('iOs에서 최근 업데이트 시간(td)이 30일 이내인 경우'),
    },
    CH: {
      title: t('커스텀하이'),
      tooltipTitle: t('TUNE720에서 설정한 상급 모수(캠페인)의 상품'),
    },
    CR: {
      title: t('커스텀리타겟팅'),
      tooltipTitle: t('TUNE720의 모수를 활용한 상품 광고'),
    },
    PF: {
      title: t('퍼포먼스광고'),
      tooltipTitle: t(
        '광고주의 방문 고객을 원하는 세그먼트로 직접 분류하여 광고를 노출'
      ),
    },
    HV: {
      title: t('원타임본매칭'),
      tooltipTitle: t('1시간 이내 본 상품을 추천'),
    },
    HE: {
      title: t('헤비리인게이지먼트'),
      tooltipTitle: t('iUm에서 최근업데이트시간(updDate)가 336시간이 지난 도메인'),
    },
    TV: {
      title: t('투데이본상품'),
      tooltipTitle: t('최근 24시간 내에 1회 본 상품'),
    },
    OSR: {
      title: t('오가닉본상품'),
      tooltipTitle: t('모비온 광고 클릭 직후 랜딩·연속 탐색을 제외하고, 검색·직접 진입·이탈 후 재진입 등 자연 유입으로 조회된 상품 모수'),
    },
    WV: {
      title: t('위클리본상품'),
      tooltipTitle: t('최근 168시간(7일) 내에 1회 본 상품'),
    },
    MV: {
      title: t('먼슬리본상품'),
      tooltipTitle: t('최근 720시간(30일) 내에 1회 본 상품'),
    },
    RC: { title: t('관심상품'), tooltipTitle: t('2회 이상 본 상품') },
    SR: { title: t('본상품'), tooltipTitle: t('1회 본 상품') },
    TP: {
      title: t('투데이보완재'),
      tooltipTitle: t('24시간 이내 전환된 상품의 보완재 상품을 최신순으로 송출'),
    },
    WP: {
      title: t('위클리보완재'),
      tooltipTitle: t('168시간 이내 전환이 일어난 상품 기준으로 다른 상품을 추천'),
    },
    SP: {
      title: t('먼솔리보완재'),
      tooltipTitle: t('전환이 일어난 상품 기준으로 다른 상품을 추천'),
    },
    HP: {
      title: t('하프보완재'),
      tooltipTitle: t('6개월(4,320시간) 이내 전환된 상품의 보완재 상품을 추천'),
    },
    HR: {
      title: t('원타임리턴'),
      tooltipTitle: t('1시간 이내  방문한 사이트를  추천'),
    },
    TR: {
      title: t('투데이리턴'),
      tooltipTitle: t('최근 24시간 내에 광고주 도메인 1~2회 방문'),
    },
    RE: {
      title: t('리인게이지먼트'),
      tooltipTitle: t('iUm에서 최근업데이트시간(updDate)가 720시간이 지난 도메인'),
    },
    WR: {
      title: t('위클리리턴'),
      tooltipTitle: t('최근 168시간(7일) 내에 광고주 도메인 1~2회 방문'),
    },
    FR: {
      title: t('먼슬리리턴'),
      tooltipTitle: t('최근 720시간(30일) 내에 광고주 도메인 1~2회 방문'),
    },
    HU: {
      title: t('헤비유저'),
      tooltipTitle: t('광고주 도메인 3회 이상 방문'),
    },
    UM: { title: t('리턴'), tooltipTitle: t('광고주 도메인 1~2회 방문') }, // 수정X 플랫폼팀에서 리턴매칭을 RM인데 UM으로 DB에 넣고 있음
    MO: { title: t('미디어'), tooltipTitle: t('매체별로 효율이 좋았던 비쇼핑 카테고리를 우선 송출하는 타겟팅') },
    PA: { title: t('쇼라베이스'), tooltipTitle: t('라이브커머스 광고주 전용으로 단시간동안 특정 매체에서만 송출') },
    AD: { title: t('베이스'), tooltipTitle: t('타겟팅이 없을때 광고 송출') },
    US: { title: t('유저유사도'), tooltipTitle: t('CDP 데이터 상으로 활동한 광고주 사이트가 2개 이상 유저들을 대상으로 추천 TOP10순으로 송출') },
  };

  useEffect(() => {
    goodsinfoAdverList()
      .then((response) => {
        const { data } = response.data;
        setAdverList(data);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: { type: 'error' },
        });
      });
    handleCustom();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (reGetData === true) {
      setReGetData(false);
    }
  }, [reGetData]);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  // header 및 body 컬럼 커스텀

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  const getParam = (newParams) => {
    if (newParams.adverId === '') {
      newParams.adverId = null;
    }
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
      handleCustom(newParams);
    }
  };

  const handleSortChange = (propsParams, orderKey) => {
    const { order, sort } = propsParams;
    const isDesc = order === orderKey && sort === 'desc';
    const converSort = isDesc ? 'asc' : 'desc';
    const newParams = {
      ...params,
      order: orderKey,
      sort: converSort,
    };
    setParams(newParams);
    handleCustom(newParams);
  };

  const handleCustom = (newParams = params) => {
    let newCustomized = { ...initCustomized };
    for (let target in list) {
      const name = target;
      const title = list[target]['title'];
      const tooltipTitle = list[target]['tooltipTitle'];
      newCustomized['ACC_' + name] = {
        makeHead: (headsMap) => {
          return (
            <SpanningTableContents
              topTitle={
                <InfoTitleArea
                  title={title}
                  tooltipTitle={tooltipTitle}
                  iconType="fainfoCircle"
                />
              }
              type="head"
            >
              <div className={classes.cellSize}>
                {getHeadObj(headsMap, 'ACC_' + name).orderFlag !== 'object' ? (
                  <SortLabelWrap
                    params={newParams}
                    label={t('누적')}
                    orderKey={'ACC_' + name}
                    onClick={(e) => handleSortChange(newParams, 'ACC_' + name)}
                  />
                ) : (
                  t('누적')
                )}
              </div>
              <div className={classes.cellSize}>
                {getHeadObj(headsMap, 'DAILY_' + name).orderFlag !==
                'object' ? (
                  <SortLabelWrap
                    params={newParams}
                    label={t('일일')}
                    orderKey={'DAILY_' + name}
                    onClick={(e) =>
                      handleSortChange(newParams, 'DAILY_' + name)
                    }
                  />
                ) : (
                  t('일일')
                )}
              </div>
              <div className={classes.cellSize}>
                {getHeadObj(headsMap, 'NEW_' + name).orderFlag !== 'object' ? (
                  <SortLabelWrap
                    params={newParams}
                    label={t('신규')}
                    orderKey={'NEW_' + name}
                    onClick={(e) => handleSortChange(newParams, 'NEW_' + name)}
                  />
                ) : (
                  t('신규')
                )}
              </div>
            </SpanningTableContents>
          );
        },
        makeBody: (rowObj, headsMap) => {
          return (
            <SpanningTableContents>
              <div className={classes.cellSize}>
                {numeral(rowObj['ACC_' + name]).format('#,##0')}
              </div>
              <div className={classes.cellSize}>
                {numeral(rowObj['DAILY_' + name]).format('#,##0')}
              </div>
              <div className={classes.cellSize}>
                {numeral(rowObj['NEW_' + name]).format('#,##0')}
              </div>
            </SpanningTableContents>
          );
        },
        headStyle: {
          borderLeft: '1px solid #e5e8eb',
          borderRight: '1px solid #e5e8eb',
        },
        bodyStyle: { borderRight: '1px solid #e5e8eb' },
      };
      newCustomized['DAILY_' + name] = { merged: true };
      newCustomized['NEW_' + name] = { merged: true };
    }
    setCustomized(newCustomized);
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          useDisplayFilter={false}
          useExternalFilter={false}
        />
        <AutoComplete
          list={adverList}
          label={t('광고주ID')}
          name="adverId"
          valueKey="USER_ID"
          labelKey="USER_ID"
          getParam={getParam}
          params={params}
        />
      </NewFilterWrap>
      <BarGraph
        params={params}
        dataReqPromise={cookieParamGraph}
        graphList={graphList}
        title={t('광고쿠키통계')}
        labelType="MMDD"
        useHideYaxis
      />
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={CookieStatisticsData}
        customized={customized}
        useTotal={false}
        useSearch={false}
      />
    </>
  );
};

export default CookieStatistics;
